import styled from "styled-components";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { cartActions } from "../../store/cart-slice";
import { saveForLaterActions } from "../../store/save-slice";
import { ProductContext } from "../../context/ProductContext";
import AddToCartAnim from "../animations/AddToCartAnim";
import { useNavigation } from "../../utils/navigation";
import { useNavigate } from "react-router-dom";
import { calculatePrice } from "../../utils/discount";
import { useTranslation } from "react-i18next";
import { AuthUserContext } from "../../context/AuthUserContext";
import SimpleContext from "../../context/SimpleContext";
import Success from "../toast/Success";

const showSaveNotification = (text) => {
  toast(<Success text={text} />, {
    position: "top-left",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    className: "custom-toast-success",
    progressClassName: "custom-progress-bar",
  });
};

const Container = styled.figure`
  background-color: var(--bg-200);
  width: 100%;
  //min-width: 220px;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl) 0 0 0;
  box-shadow: var(--shadow-medium);
  border-radius: 8px;
`;

const Picture = styled.picture`
  display: flex; // Omogućavanje flex layout-a za centriranje slike
  align-items: center;
  justify-content: center;
  //height: 240px;
  cursor: pointer;
  width: 92%;
  position: relative;
  overflow: hidden;
  background-color: var(--bg-100);
  border-radius: var(--border-radius-base);

  height: 140px;
  max-height: 140px;
  min-height: 140px;

  &:hover {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    .primary-image {
      display: none;
      display: ${(props) => (props.$secondary_image ? "none" : "block")};
    }

    .secondary-image {
      display: block;
      display: ${(props) => (props.$secondary_image ? "block" : "none")};
    }
  }
  @media (min-width: 768px) {
    height: 200px;
    max-height: 200px;
    min-height: 200px;
  }
`;

const Image = styled.img`
  max-width: 90%;
  max-height: 90%;
  width: auto;
  height: auto;

  object-fit: contain;
  display: block;
  margin: 0 auto;
  opacity: ${({ $outOfStock }) => ($outOfStock ? 0.45 : 1)};
  filter: ${({ $outOfStock }) => ($outOfStock ? "grayscale(1)" : "none")};
  &.secondary-image {
    display: none;
  }
`;

const OutOfStockBadge = styled.span`
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  z-index: 1;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  color: var(--text-200);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const BottomContainer = styled.div`
  width: 100%;
  background-color: var(--bg-100);
  padding-bottom: 14px;
`;
const Bottom = styled.div`
  width: 90%;
  margin: 0 auto;
`;

const ProductTitle = styled.h5`
  margin: 12px 0 0 0;
  color: var(--text-100);
  font-weight: 700;
  height: calc(var(--font-size-base) * 2 + 8px);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
  &:hover {
    color: var(--primary-200);
  }
  text-align: left;
  width: 100%;
  @media (min-width: 768px) {
    font-size: var(--font-size-base);
    height: calc(var(--font-size-base) * 2 + 20px);
  }
`;

const DropdownContainer = styled.div`
  display: grid;
  gap: 8px;
  @media (min-width: 768px) {
    grid-template-columns: 3fr 1fr;
    gap: 8px;
  }
`;

const DropdownHeader = styled.div`
  position: relative;
  width: calc(100% - 2px);
  font-size: var(--font-size-base);
  color: var(--text-200);
  background-color: var(--bg-100);

  border: 1px solid var(--bg-300);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-100);
  }
  max-height: 40px;
  @media (max-width: 768px) {
    border-radius: 8px;
  }
`;
const DropdownHeaderInner = styled.div`
  width: 90%;
  height: 100%;
  min-height: 40px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-small);
`;

const DropdownPrice = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 700;
  color: var(--text-100);
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: -1px;
  width: calc(100%);
  z-index: 9;
  background-color: var(--bg-200);
  border: 1px solid var(--bg-300);
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};

  position: fixed;
  top: ${({ dropdownPosition }) => dropdownPosition.top}px;
  left: ${({ dropdownPosition }) => dropdownPosition.left}px;
  width: ${({ dropdownPosition }) => dropdownPosition.width}px;
  transition: transform 0s ease;
`;

const DropdownItem = styled.div`
  font-size: var(--font-size-base);
  padding: 10px 15px;
  color: ${({ isSelected }) => (isSelected ? "var(--primary-200)" : "var(--text-200)")};
  font-weight: ${({ isSelected }) => (isSelected ? "700" : "400")};
  cursor: pointer;
  background-color: ${({ isSelected }) =>
    isSelected ? "var(--bg-200)" : "var(--bg-100)"};
  border-left: 3px solid
    ${({ isSelected }) => (isSelected ? "var(--primary-200)" : "transparent")};
  &:hover {
    background-color: var(--bg-200);
  }
  border-bottom: 1px solid var(--bg-300);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-small);
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;
const ButtonWrapper = styled.div`
  position: relative; /* Roditeljski kontejner koji omogućava apsolutno pozicioniranje za decu */
  display: inline-block; /* Omogućava kontejneru da se prilagodi veličini sadržaja */
  width: 100%;
  text-align: center;
  //margin-top: 14px;
`;
const Button = styled.button`
  width: 100%;
  height: 100%;
  text-transform: capitalize;
  z-index: 1;
  font-size: var(--font-size-small);

  //border-radius: 0 0 12px 12px;
  @media (min-width: 768px) {
    //border-radius: 0 12px 12px 0;
  }
  border-radius: 0;
  @media (max-width: 768px) {
    border-radius: 8px;
  }
`;
const SVGContainer = styled.div`
  position: relative;
  align-self: flex-end;
  background-color: var(--bg-100);
  border-radius: 50%;
  padding: 4px;
  border: 1px solid var(--bg-100);
  margin: -30px 4px 0 0;
  display: flex;
  align-items: center;
  z-index: 10;
  transition: all 0.3s ease;
  &:hover {
    border: 1px solid var(--text-100);
    & > span {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }
`;
const SaveForLaterSVG = styled.svg`
  cursor: pointer;
`;
const TooltipText = styled.span`
  position: absolute;
  bottom: 110%; /* Position above the icon */
  right: 0%;

  background-color: var(--text-200);
  color: var(--bg-100);
  padding: 6px 10px;
  border-radius: 4px;
  font-size: var(--font-size-small);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.5s ease;
  z-index: 20;

  /* Optional arrow styling for the tooltip */
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 10%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: var(--text-200) transparent transparent transparent;
  }
`;

const ProductCard = ({ product, isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const { authTokens } = useContext(AuthUserContext);
  const { showLoginPopup } = useContext(SimpleContext);
  const [selectedQuantity, setSelectedQuantity] = useState(10);

  const quantities = [1, 10, 30, 50];

  const dispatch = useDispatch();
  const { setIsCartOpen, maxAttributes, currencyTag } =
    useContext(ProductContext);
  const { goToProduct } = useNavigation();
  const navigate = useNavigate();

  const [isAnimating, setIsAnimating] = useState(false);

  const saveForLaterItems = useSelector(
    (state) => state.saveForLater.itemsList
  );
  const isSaved = saveForLaterItems.some(
    (item) => item.product.id === product.id
  );

  const toggleSaveForLater = () => {
    if (isSaved) {
      dispatch(saveForLaterActions.removeFromSaveForLater(product.id));
    } else {
      dispatch(
        saveForLaterActions.addToSaveForLater({
          product: {
            ...product,
            discount_price: calculatePrice(product.price, 1),
          },
          quantity: 1,
        })
      );
      showSaveNotification(t("CART.SAVED_FOR_LATER"));
    }
  };

  const addToCart = (selectedQuantity) => {
    dispatch(
      cartActions.addToCart({
        product: product,
        quantity: selectedQuantity,
      })
    );
    setIsCartOpen(true);
    setIsAnimating(true);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  const discountedPrice = calculatePrice(product.price, selectedQuantity);

  // Calculate savings
  const savingsPercentage = Math.round(
    ((product.price - discountedPrice) / product.price) * 100
  );

  const primaryImage = product.images?.find((img) => img.is_primary);
  const secondaryImage = product.images?.find((img) => !img.is_primary);
  const isOutOfStock = product.is_in_stock === "out_of_stock";

  const handleSelect = (item) => {
    setSelectedQuantity(item);
    setIsOpen(false);
  };

  const dropdownTriggerRef = useRef(null);
  const dropdownElementRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  function getScrollableParent(element) {
    let parent = element.parentElement;
    while (parent) {
      if (parent.dataset.scrollable === "true") {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (dropdownTriggerRef.current) {
      const rect = dropdownTriggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom, // Donja ivica dugmeta
        left: rect.left, // Leva ivica dugmeta
        width: rect.width,
      });
    }
  };

  const updateDropdownPosition = () => {
    if (dropdownTriggerRef.current && dropdownElementRef.current) {
      const rect = dropdownTriggerRef.current.getBoundingClientRect();

      const top = rect.bottom;
      const left = rect.left;
      const width = rect.width;

      dropdownElementRef.current.style.top = `${top}px`;
      dropdownElementRef.current.style.left = `${left}px`;
      dropdownElementRef.current.style.width = `${width}px`;
    }
  };

  useEffect(() => {
    updateDropdownPosition(); // Inicijalno postavljanje

    window.addEventListener("scroll", updateDropdownPosition, {
      passive: true,
    });
    window.addEventListener("resize", updateDropdownPosition);

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen]);
  return (
    <Container>
      <Picture
        onClick={() =>
          navigate(goToProduct(product.category_name, product.name))
        }
        //$secondary_image={secondaryImage}
      >
        {isOutOfStock && (
          <OutOfStockBadge>{t("BUTTONS.AVAILABLE_SOON")}</OutOfStockBadge>
        )}
        {primaryImage && (
          <Image
            src={primaryImage.thumbnail}
            alt={`${product.name}`}
            srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="primary-image"
            $outOfStock={isOutOfStock}
          />
        )}
        {secondaryImage && (
          <Image
            src={secondaryImage.thumbnail}
            alt={` ${product.name}`}
            srcSet={`${secondaryImage.thumbnail} 320w, ${secondaryImage.large} 480w, ${secondaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="secondary-image"
            $outOfStock={isOutOfStock}
          />
        )}
      </Picture>
      <SVGContainer
        onClick={(event) => {
          event.stopPropagation();
          if (authTokens) {
            toggleSaveForLater();
          } else {
            showLoginPopup();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={
          isSaved ? t("BUTTONS.REMOVE") : t("BUTTONS.SAVE_FOR_LATER")
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            if (authTokens) {
              toggleSaveForLater();
            } else {
              showLoginPopup();
            }
          }
        }}
      >
        {isSaved ? (
          <SaveForLaterSVG
            width="30px"
            height="30px"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.75 6L7.5 5.25H16.5L17.25 6V19.3162L12 16.2051L6.75 19.3162V6Z"
              fill="var(--accent-200)"
              stroke="var(--accent-200)"
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </SaveForLaterSVG>
        ) : (
          <SaveForLaterSVG
            width="30px"
            height="30px"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.75 6L7.5 5.25H16.5L17.25 6V19.3162L12 16.2051L6.75 19.3162V6ZM8.25 6.75V16.6838L12 14.4615L15.75 16.6838V6.75H8.25Z"
              fill="var(--text-200)"
            />
          </SaveForLaterSVG>
        )}
        <TooltipText>
          {isSaved ? t("BUTTONS.REMOVE") : t("BUTTONS.SAVE_FOR_LATER")}
        </TooltipText>
      </SVGContainer>
      <BottomContainer>
        <Bottom>
          <ProductTitle
            onClick={() =>
              navigate(goToProduct(product.category_name, product.name))
            }
          >
            {product.name}
          </ProductTitle>
          {isOpen !== undefined && (
            <DropdownContainer>
              <DropdownHeader ref={dropdownTriggerRef} onClick={handleToggle}>
                <DropdownHeaderInner>
                  <span>
                    {selectedQuantity} {t("PRODUCT_CARD.PACK")}
                  </span>
                  <DropdownPrice>
                    {currencyTag}
                    {calculatePrice(product.price, selectedQuantity)}
                    <svg
                      fill="var(--text-200)"
                      width="20px"
                      height="20px"
                      viewBox="0 0 1024 1024"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M759.2 419.8L697.4 358 512 543.4 326.6 358l-61.8 61.8L512 667z" />
                    </svg>
                  </DropdownPrice>
                </DropdownHeaderInner>
                <DropdownList
                  isOpen={isOpen}
                  dropdownPosition={dropdownPosition}
                  ref={dropdownElementRef}
                >
                  {quantities.map((item) => (
                    <DropdownItem
                      key={item}
                      isSelected={item === selectedQuantity}
                      onClick={() => handleSelect(item)}
                    >
                      {item === 1 ? (
                        <span>{t("PRODUCT_CARD.DOSE")}</span>
                      ) : (
                        <span>
                          {item} {t("PRODUCT_CARD.PACK")}
                        </span>
                      )}
                      <span>
                        {currencyTag}
                        {(calculatePrice(product.price, item) * item).toFixed(
                          2
                        )}{" "}
                      </span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              </DropdownHeader>
              <ButtonWrapper>
                <Button
                  onClick={() => addToCart(selectedQuantity)}
                  disabled={isOutOfStock}
                >
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14 2C14 1.44772 13.5523 1 13 1C12.4477 1 12 1.44772 12 2V8.58579L9.70711 6.29289C9.31658 5.90237 8.68342 5.90237 8.29289 6.29289C7.90237 6.68342 7.90237 7.31658 8.29289 7.70711L12.2929 11.7071C12.6834 12.0976 13.3166 12.0976 13.7071 11.7071L17.7071 7.70711C18.0976 7.31658 18.0976 6.68342 17.7071 6.29289C17.3166 5.90237 16.6834 5.90237 16.2929 6.29289L14 8.58579V2ZM1 3C1 2.44772 1.44772 2 2 2H2.47241C3.82526 2 5.01074 2.90547 5.3667 4.21065L5.78295 5.73688L7.7638 13H18.236L20.2152 5.73709C20.3604 5.20423 20.9101 4.88998 21.4429 5.03518C21.9758 5.18038 22.29 5.73006 22.1448 6.26291L20.1657 13.5258C19.9285 14.3962 19.1381 15 18.236 15H8V16C8 16.5523 8.44772 17 9 17H16.5H18C18.5523 17 19 17.4477 19 18C19 18.212 18.934 18.4086 18.8215 18.5704C18.9366 18.8578 19 19.1715 19 19.5C19 20.8807 17.8807 22 16.5 22C15.1193 22 14 20.8807 14 19.5C14 19.3288 14.0172 19.1616 14.05 19H10.95C10.9828 19.1616 11 19.3288 11 19.5C11 20.8807 9.88071 22 8.5 22C7.11929 22 6 20.8807 6 19.5C6 18.863 6.23824 18.2816 6.63048 17.8402C6.23533 17.3321 6 16.6935 6 16V14.1339L3.85342 6.26312L3.43717 4.73688C3.31852 4.30182 2.92336 4 2.47241 4H2C1.44772 4 1 3.55228 1 3ZM16 19.5C16 19.2239 16.2239 19 16.5 19C16.7761 19 17 19.2239 17 19.5C17 19.7761 16.7761 20 16.5 20C16.2239 20 16 19.7761 16 19.5ZM8 19.5C8 19.2239 8.22386 19 8.5 19C8.77614 19 9 19.2239 9 19.5C9 19.7761 8.77614 20 8.5 20C8.22386 20 8 19.7761 8 19.5Z"
                      fill="var(--bg-100)"
                    />
                  </svg>
                </Button>
                {isAnimating && (
                  <AddToCartAnim
                    isAnimating={isAnimating}
                    onComplete={handleAnimationComplete}
                  />
                )}
              </ButtonWrapper>
            </DropdownContainer>
          )}
        </Bottom>
      </BottomContainer>
    </Container>
  );
};

export default ProductCard;
